import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TokenProvider } from './context/TokenContext';
import { PWAProvider, usePWAContext } from './context/PWAContext';
import { PWAInstallPrompt, OfflineIndicator, UpdateBanner } from './components/PWAInstallPrompt';
import SplashScreen from './components/SplashScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { setupGlobalErrorHandlers } from './lib/errorMonitoring';

import './App.css';

// Initialize global error handlers
setupGlobalErrorHandlers();

// Critical pages loaded synchronously for faster mobile experience
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';

// Lazy load secondary pages for better bundle splitting
const Chat = lazy(() => import('./pages/Chat'));
const Consultation = lazy(() => import('./pages/Consultation'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Features = lazy(() => import('./pages/Features'));
const Affaires = lazy(() => import('./pages/Affaires'));
const AffaireDetail = lazy(() => import('./pages/AffaireDetail'));
const Clients = lazy(() => import('./pages/Clients'));
const Calendrier = lazy(() => import('./pages/Calendrier'));
const Documents = lazy(() => import('./pages/Documents'));
const Notes = lazy(() => import('./pages/Notes'));
const Recherche = lazy(() => import('./pages/Recherche'));
const Facturation = lazy(() => import('./pages/Facturation'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
const ConfigAPI = lazy(() => import('./pages/ConfigAPI'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Setup = lazy(() => import('./pages/Setup'));
const Download = lazy(() => import('./pages/Download'));

// Admin pages
const Marketing = lazy(() => import('./pages/Admin/Marketing'));
const Monitoring = lazy(() => import('./pages/Admin/Monitoring'));

// Public marketing pages
const TrialSignup = lazy(() => import('./pages/TrialSignup'));

// Legal pages
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const CGV = lazy(() => import('./pages/CGV'));

// Client Portal pages
const ClientLogin = lazy(() => import('./pages/ClientPortal/ClientLogin'));
const ClientRegister = lazy(() => import('./pages/ClientPortal/ClientRegister'));
const ClientDashboard = lazy(() => import('./pages/ClientPortal/ClientDashboard'));
const ClientMessages = lazy(() => import('./pages/ClientPortal/ClientMessages'));
const ClientNotifications = lazy(() => import('./pages/ClientPortal/ClientNotifications'));
const ClientDocuments = lazy(() => import('./pages/ClientPortal/ClientDocuments'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen min-h-screen-dynamic bg-[#0F172A] flex items-center justify-center safe-y">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-[#B69D74]/30 border-t-[#B69D74] rounded-full animate-spin" />
      <span className="text-[#94A3B8] text-sm">Chargement...</span>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - loaded synchronously */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><Pricing /></Suspense>} />
      <Route path="/features" element={<Suspense fallback={<PageLoader />}><Features /></Suspense>} />
      <Route path="/setup" element={<Suspense fallback={<PageLoader />}><Setup /></Suspense>} />
      <Route path="/download" element={<Suspense fallback={<PageLoader />}><Download /></Suspense>} />
      <Route path="/telecharger" element={<Suspense fallback={<PageLoader />}><Download /></Suspense>} />
      
      {/* Public marketing pages */}
      <Route path="/essai-gratuit" element={<Suspense fallback={<PageLoader />}><TrialSignup /></Suspense>} />
      
      {/* Legal pages */}
      <Route path="/mentions-legales" element={<Suspense fallback={<PageLoader />}><MentionsLegales /></Suspense>} />
      <Route path="/politique-confidentialite" element={<Suspense fallback={<PageLoader />}><PolitiqueConfidentialite /></Suspense>} />
      <Route path="/confidentialite" element={<Suspense fallback={<PageLoader />}><PolitiqueConfidentialite /></Suspense>} />
      <Route path="/cgv" element={<Suspense fallback={<PageLoader />}><CGV /></Suspense>} />
      <Route path="/trial" element={<Suspense fallback={<PageLoader />}><TrialSignup /></Suspense>} />
      
      {/* Client Portal public routes */}
      <Route path="/espace-client/connexion" element={<Suspense fallback={<PageLoader />}><ClientLogin /></Suspense>} />
      <Route path="/espace-client/inscription" element={<Suspense fallback={<PageLoader />}><ClientRegister /></Suspense>} />
      <Route path="/espace-client/activer" element={<Suspense fallback={<PageLoader />}><ClientRegister /></Suspense>} />
      <Route path="/espace-client" element={<Suspense fallback={<PageLoader />}><ClientDashboard /></Suspense>} />
      <Route path="/espace-client/messages" element={<Suspense fallback={<PageLoader />}><ClientMessages /></Suspense>} />
      <Route path="/espace-client/messages/:affaireId" element={<Suspense fallback={<PageLoader />}><ClientMessages /></Suspense>} />
      <Route path="/espace-client/notifications" element={<Suspense fallback={<PageLoader />}><ClientNotifications /></Suspense>} />
      <Route path="/espace-client/documents" element={<Suspense fallback={<PageLoader />}><ClientDocuments /></Suspense>} />
      
      <Route path="/paiement/succes" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>
        </ProtectedRoute>
      } />

      {/* Main protected route - loaded synchronously */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Lazy loaded protected routes */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Chat /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/consultation" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Consultation /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/affaires" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Affaires /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/affaires/:id" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><AffaireDetail /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Clients /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/calendrier" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Calendrier /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Documents /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/notes" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Notes /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/recherche" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Recherche /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/facturation" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Facturation /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Settings /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Admin /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/config-api" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><ConfigAPI /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/roadmap" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Roadmap /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/marketing" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Marketing /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/monitoring" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><Monitoring /></Suspense>
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/**
 * Composant pour gérer les mises à jour PWA
 */
function PWAUpdateManager() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Écouter les événements de mise à jour du SW
    const handleUpdateAvailable = (event) => {
      setRegistration(event.detail?.registration);
      setShowUpdate(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleUpdate = async () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    // La page sera rechargée via l'event controllerchange
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  if (!showUpdate) return null;

  return <UpdateBanner onUpdate={handleUpdate} onDismiss={handleDismiss} />;
}

/**
 * Composant principal de l'application avec gestion du splash screen
 */
function AppContent() {
  const [showSplash, setShowSplash] = useState(true);
  const { isStandalone } = usePWAContext();

  // N'afficher le splash que en mode PWA (standalone)
  const shouldShowSplash = showSplash && isStandalone;

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <>
      {shouldShowSplash && (
        <SplashScreen 
          minDisplayTime={2800}
          onComplete={handleSplashComplete}
          version="2.0"
        />
      )}
      <AuthProvider>
        <TokenProvider>
          <BrowserRouter>
            <OfflineIndicator />
            <PWAUpdateManager />
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <PWAInstallPrompt />
          </BrowserRouter>
        </TokenProvider>
      </AuthProvider>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <PWAProvider>
        <AppContent />
      </PWAProvider>
    </ErrorBoundary>
  );
}

export default App;
